import { LvfAppModule, LvfAppPlatform } from "@kiraah-shared/common";
import type { IdentityProfilData } from "../models/mobile-profile.models";
import { buildMobileModuleAccessFlags } from "../profile/helpers/mobile-module-access";
import { mobileResolveIdentityAccessFlags } from "../profile/helpers/mobile-operation-access";
import { mobilePath } from "../shell/mobile-routes.constants";

export const ACADEMY_MOBILE_APP_CODE = "academy-mobile";
export const LEGACY_ELEARNING_MOBILE_APP_CODE = "elearning-mobile";
const STUDENT_SURVEYS_ROUTE = mobilePath("elearning/student/surveys");
const MENTOR_SURVEYS_ROUTE = mobilePath("elearning/mentor/surveys");
const CONTACT_SURVEYS_ROUTE = mobilePath("elearning/contact/surveys");
const STUDENT_SESSIONS_ROUTE = mobilePath("elearning/student/sessions");
const MENTOR_SESSIONS_ROUTE = mobilePath("elearning/mentor/sessions");
const CONTACT_SESSIONS_ROUTE = mobilePath("elearning/contact/sessions");
const HUB_SESSIONS_ROUTE = mobilePath("elearning/sessions");
const CATALOG_ROUTE = mobilePath("elearning");

export function collectActiveApplicationMenuUrls(
  profileData: IdentityProfilData | null | undefined,
  activeTenantAppUid: string | null | undefined
): readonly string[] {
  if (!profileData || !activeTenantAppUid) {
    return [];
  }

  const activeApp = profileData.Applications.find((app) => String(app.Uid) === activeTenantAppUid);
  const urls: string[] = [];
  for (const menu of activeApp?.Menus ?? []) {
    if (menu.Platform === LvfAppPlatform.WEB) {
      continue;
    }
    for (const item of menu.Items ?? []) {
      if (item.Platform === LvfAppPlatform.WEB) {
        continue;
      }
      const url = item.Url?.trim();
      if (url) {
        urls.push(url);
      }
    }
  }
  return urls;
}

function normalizeComparableUrl(url: string): string {
  return url.split("?")[0].replace(/\/+$/, "").toLowerCase();
}

function menuIncludesSurveys(
  menuUrls: readonly string[],
  segment: "student/surveys" | "mentor/surveys" | "contact/surveys"
): boolean {
  const needle = segment.toLowerCase();
  return menuUrls.some((url) => normalizeComparableUrl(url).includes(needle));
}

function menuIncludesSessions(
  menuUrls: readonly string[],
  segment: "student/sessions" | "mentor/sessions" | "contact/sessions"
): boolean {
  const needle = segment.toLowerCase();
  return menuUrls.some((url) => normalizeComparableUrl(url).includes(needle));
}

/** Taskbar Sessions → hub ou liste participant (apprenant / formateur). */
export function resolveMobileElearningSessionsRoute(
  profileData: IdentityProfilData | null | undefined,
  activeTenantAppUid: string | null | undefined,
  activeAppCode: string | undefined
): string {
  const { isRoot, isAdmin } = mobileResolveIdentityAccessFlags(profileData?.Identity);
  if (isRoot || isAdmin) {
    return HUB_SESSIONS_ROUTE;
  }

  const menuUrls = collectActiveApplicationMenuUrls(profileData, activeTenantAppUid);
  const hasStudentSessions = menuIncludesSessions(menuUrls, "student/sessions");
  const hasMentorSessions = menuIncludesSessions(menuUrls, "mentor/sessions");
  const hasContactSessions = menuIncludesSessions(menuUrls, "contact/sessions");

  if (!hasStudentSessions && !hasMentorSessions && !hasContactSessions) {
    return HUB_SESSIONS_ROUTE;
  }

  if (hasMentorSessions && !hasStudentSessions && !hasContactSessions) {
    return MENTOR_SESSIONS_ROUTE;
  }

  if (hasContactSessions && !hasStudentSessions && !hasMentorSessions) {
    return CONTACT_SESSIONS_ROUTE;
  }

  const studentAccess = buildMobileModuleAccessFlags(
    profileData,
    activeTenantAppUid,
    LvfAppModule.ELEARNING_STUDENT_SESSION
  );
  const mentorAccess = buildMobileModuleAccessFlags(
    profileData,
    activeTenantAppUid,
    LvfAppModule.ELEARNING_MENTOR_SESSION
  );
  const contactAccess = buildMobileModuleAccessFlags(
    profileData,
    activeTenantAppUid,
    LvfAppModule.ELEARNING_THIRD_PART_CONTACT_SESSION
  );

  if (mentorAccess.hasReadAccess && !studentAccess.hasReadAccess && !contactAccess.hasReadAccess) {
    return MENTOR_SESSIONS_ROUTE;
  }

  if (contactAccess.hasReadAccess && !studentAccess.hasReadAccess && !mentorAccess.hasReadAccess) {
    return CONTACT_SESSIONS_ROUTE;
  }

  if (hasStudentSessions) {
    return STUDENT_SESSIONS_ROUTE;
  }

  if (hasContactSessions) {
    return CONTACT_SESSIONS_ROUTE;
  }

  return HUB_SESSIONS_ROUTE;
}

/** Taskbar Quiz → catalogue E-Academy ou enquêtes participant. */
export function resolveMobileElearningQuizRoute(
  profileData: IdentityProfilData | null | undefined,
  activeTenantAppUid: string | null | undefined,
  activeAppCode: string | undefined
): string {
  const menuUrls = collectActiveApplicationMenuUrls(profileData, activeTenantAppUid);
  const hasStudentSurveys = menuIncludesSurveys(menuUrls, "student/surveys");
  const hasMentorSurveys = menuIncludesSurveys(menuUrls, "mentor/surveys");
  const hasContactSurveys = menuIncludesSurveys(menuUrls, "contact/surveys");

  if (!hasStudentSurveys && !hasMentorSurveys && !hasContactSurveys) {
    return CATALOG_ROUTE;
  }

  if (hasMentorSurveys && !hasStudentSurveys && !hasContactSurveys) {
    return MENTOR_SURVEYS_ROUTE;
  }

  if (hasContactSurveys && !hasStudentSurveys && !hasMentorSurveys) {
    return CONTACT_SURVEYS_ROUTE;
  }

  const studentAccess = buildMobileModuleAccessFlags(
    profileData,
    activeTenantAppUid,
    LvfAppModule.ELEARNING_STUDENT_SESSION
  );
  const mentorAccess = buildMobileModuleAccessFlags(
    profileData,
    activeTenantAppUid,
    LvfAppModule.ELEARNING_MENTOR_SESSION
  );
  const contactAccess = buildMobileModuleAccessFlags(
    profileData,
    activeTenantAppUid,
    LvfAppModule.ELEARNING_THIRD_PART_CONTACT_SESSION
  );

  if (mentorAccess.hasReadAccess && !studentAccess.hasReadAccess && !contactAccess.hasReadAccess) {
    return MENTOR_SURVEYS_ROUTE;
  }

  if (contactAccess.hasReadAccess && !studentAccess.hasReadAccess && !mentorAccess.hasReadAccess) {
    return CONTACT_SURVEYS_ROUTE;
  }

  return STUDENT_SURVEYS_ROUTE;
}
